"use client";

import { Button } from "@evenli-platform/ui/components/button-onboarding";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/onboarding/form-onboarding";
import { Input } from "@/components/ui/onboarding/input-onboarding";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { useTranslations } from "next-intl";

import {
  resetPassword,
  confirmResetPassword,
  type ConfirmResetPasswordInput,
  AuthError,
} from "aws-amplify/auth";
import {
  codeRequestFormSchema,
  CodeRequestSchemaType,
  newPasswordSchema,
  NewPasswordSchemaType,
} from "@/lib/types";
import {
  getResetPasswordConfirmError,
  getResetPasswordError,
} from "@/lib/cognito/util";
import Link from "next/link";

export const ForgotPasswordForm = () => {
  const router = useRouter();
  const t = useTranslations("Dashboard");
  const tE = useTranslations("Errors");
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);

  async function handleResetPassword({
    email: username,
  }: CodeRequestSchemaType) {
    try {
      await resetPassword({ username });
      setIsCodeSent(true);
    } catch (error) {
      if (error instanceof AuthError) {
        const errorMessage = getResetPasswordError(error);

        if (errorMessage) {
          toast.error(tE("something_went_wrong"), {
            description: tE(errorMessage),
          });

          return;
        }
      }

      toast.error(tE("something_went_wrong"), {
        description: tE("something_went_wrong_please_try_again"),
      });
    }
  }

  async function handleConfirmResetPassword({
    username,
    confirmationCode,
    newPassword,
  }: ConfirmResetPasswordInput) {
    try {
      await confirmResetPassword({ username, confirmationCode, newPassword });

      toast.success(t("password_reset_successfully"));
      router.push("/sign-in");
    } catch (error) {
      if (error instanceof AuthError) {
        const errorMessage = getResetPasswordConfirmError(error);

        if (errorMessage) {
          toast.error(tE("something_went_wrong"), {
            description: tE(errorMessage),
          });

          return;
        }
      }

      toast.error(tE("something_went_wrong"), {
        description: tE("something_went_wrong_please_try_again"),
      });
    }
  }

  const formForCodeRequest = useForm<CodeRequestSchemaType>({
    resolver: zodResolver(codeRequestFormSchema),
    defaultValues: {
      email: "",
    },
  });

  const formForNewPassword = useForm<NewPasswordSchemaType>({
    resolver: zodResolver(newPasswordSchema),
    defaultValues: {
      code: "",
      password: "",
      confirmPassword: "",
    },
  });

  const onPasswordReset = async (data: CodeRequestSchemaType) => {
    setUserEmail(data.email);
    await handleResetPassword({
      email: data.email,
    });
  };

  const onNewPassword = async (data: NewPasswordSchemaType) => {
    await handleConfirmResetPassword({
      username: userEmail as string,
      confirmationCode: data.code,
      newPassword: data.password,
    });
  };

  return (
    <div className="max-sm:w-[calc(100%-50px)] my-[25px] w-[460px] p-[30px] max-sm:p-[20px] flex flex-col justify-start items-start gap-5 rounded-[10px] max-sm:rounded-[15px] border-2 border-opacity-20 border-white bg-white bg-opacity-10">
      <div className="w-full flex flex-col justify-start items-start gap-2.5">
        <div className="text-[26px] font-bold">{t("forgot_password")}</div>
        <div className="text-sm text-opacity-70">
          {isCodeSent
            ? t("enter_the_code_you_recieved_in_your_email")
            : t("write_your_email_and_we_will_send_you_a_code")}
          .
        </div>
      </div>
      <Form {...formForNewPassword}>
        <form
          onSubmit={formForNewPassword.handleSubmit(onNewPassword)}
          className={`w-full space-y-2 ${!isCodeSent ? "hidden" : ""}`}
        >
          <FormField
            control={formForNewPassword.control}
            name="code"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("code_from_email")}</FormLabel>
                <FormControl>
                  <Input type="text" placeholder="123456" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={formForNewPassword.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("new_password")}</FormLabel>
                <FormControl>
                  <Input type="password" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={formForNewPassword.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("confirm_password")}</FormLabel>
                <FormControl>
                  <Input type="password" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button
            className="ml-auto w-full"
            type="submit"
            loading={formForNewPassword.formState.isSubmitting}
          >
            {t("change_password")}
          </Button>
        </form>
      </Form>
      <Form {...formForCodeRequest}>
        <form
          onSubmit={formForCodeRequest.handleSubmit(onPasswordReset)}
          className={`w-full space-y-2 ${isCodeSent ? "hidden" : ""}`}
        >
          <FormField
            control={formForCodeRequest.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("email")}</FormLabel>
                <FormControl>
                  <Input type="email" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button
            className="ml-auto w-full"
            type="submit"
            loading={formForCodeRequest.formState.isSubmitting}
          >
            {t("reset_password")}
          </Button>
        </form>
      </Form>
      <div className="flex-grow w-full h-[0px] mt-5 border border-zinc-200 border-opacity-20"></div>
      <div className="flex w-full justify-center items-center">
        <Button variant="ghost" asChild>
          <Link href="/sign-in">{t("back_to_login")}</Link>
        </Button>
      </div>
    </div>
  );
};
